import { lotusHealthSchemas } from '@lotushealth/lotushealth_js_sdk';
import { ArrowLeftRight, Eye, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';

import type { RxFormApi } from '../../model/types';

import { usePatientEligibilitiesQuery } from '@/entities/eligibility';
import { useDispenseUnitsQuery, usePatientFormularyQuery } from '@/entities/rx';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { FormField } from '@/shared/ui/primitives';

import { deriveFormularyDisplayState } from '../../model/formularyDisplayPolicy';
import { useAlternativeSwitcher } from '../../model/useAlternativeSwitcher';
import { usePatientFormularyParams } from '../../model/usePatientFormularyParams';
import { AlternativesDialog } from '../AlternativesDialog';
import { FormularyDetailDialog } from '../FormularyDetailDialog';
import { FormularyStatusBadge } from '../FormularyStatusBadge';
import { MedicationMetaBadges } from '../MedicationMetaBadges';
import {
  DiagnosisSelector,
  IngredientsEditor,
  MedicationSelector,
  PharmacySelector,
} from '../selectors';

interface Props {
  form: RxFormApi;
  userId: string;
  /**
   * Optional override pinning the formulary lookup to a specific
   * ``PatientEligibilityId``. ``null`` means "auto" (server picks the
   * first active coverage that covers the prescription's channel).
   *
   * When the host doesn't pass ``onFormularyEligibilityOverrideChange``
   * (e.g. the rxchange / refill modify wizard which has no place for
   * the picker), the dropdown is hidden entirely.
   */
  formularyEligibilityOverride?: number | null;
  onFormularyEligibilityOverrideChange?: (value: number | null) => void;
}

const FORMULARY_ELIGIBILITY_AUTO_VALUE = 'auto';

const statusOptions = [
  lotusHealthSchemas.MedicationStatusTInput.Active,
  lotusHealthSchemas.MedicationStatusTInput.Inactive,
  lotusHealthSchemas.MedicationStatusTInput.Discontinued,
].map((v) => ({ value: v, label: v }));

export function DetailsStep({
  form,
  userId,
  formularyEligibilityOverride = null,
  onFormularyEligibilityOverrideChange,
}: Props) {
  const isCoded = form.state.values.kind === 'coded';
  const dispenseUnitsQuery = useDispenseUnitsQuery();
  const eligibilitiesQuery = usePatientEligibilitiesQuery(userId);
  const formularyParams = usePatientFormularyParams(form, formularyEligibilityOverride);
  const formularyQuery = usePatientFormularyQuery({ userId, params: formularyParams });

  const [alternativesOpen, setAlternativesOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const lastAutoDispenseUnitIdRef = useRef<string | null>(null);
  const { selectAlternative, isSwitching } = useAlternativeSwitcher({
    form,
    lastAutoDispenseUnitIdRef,
  });

  const selectedMedication =
    form.state.values.kind === 'coded' ? form.state.values.selectedMedication : null;
  const formulary = formularyQuery.data?.formulary;
  const display = deriveFormularyDisplayState({ formulary, medication: selectedMedication });
  const alternativeCount =
    display.payerAlternatives.length + display.therapeuticAlternatives.length;
  const hasSelectedMedication =
    form.state.values.kind === 'coded' && Boolean(form.state.values.selectedMedication);
  const isFormularyFetching = formularyQuery.isFetching;
  const showAlternativesButton = hasSelectedMedication && alternativeCount > 0;
  const currentMedicationName =
    selectedMedication?.DisplayName ?? selectedMedication?.DrugName ?? '—';

  const dispenseUnitOptions =
    dispenseUnitsQuery.data
      ?.filter((u) => u.StandardDispenseUnitTypeID != null)
      .map((u) => ({
        value: String(u.StandardDispenseUnitTypeID),
        label: `${u.Name ?? 'Unit'}${u.Abbreviation ? ` (${u.Abbreviation})` : ''}`,
      })) ?? [];
  const dispenseUnitDescription = dispenseUnitsQuery.isPending
    ? 'Loading units…'
    : dispenseUnitsQuery.isError
      ? 'Could not load units.'
      : dispenseUnitOptions.length === 0
        ? 'No units available.'
        : undefined;
  const dispenseUnitRetryButton = dispenseUnitsQuery.isError ? (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={() => void dispenseUnitsQuery.refetch()}
    >
      Retry
    </Button>
  ) : null;
  const dispenseUnitField = (
    <FormField
      form={form}
      type="select"
      name="dispenseUnitId"
      label=""
      description={dispenseUnitDescription}
      options={dispenseUnitOptions}
      placeholder="Unit"
      triggerClassName="w-full"
      afterTrigger={dispenseUnitRetryButton}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {isCoded ? (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-4 items-end gap-3">
            <div className="col-span-3 min-w-0">
              <MedicationSelector
                form={form}
                lastAutoDispenseUnitIdRef={lastAutoDispenseUnitIdRef}
              />
            </div>
            <div className="col-span-1 min-w-0">{dispenseUnitField}</div>
          </div>
          {hasSelectedMedication ? (
            <div className="flex flex-col gap-1.5">
              <MedicationMetaBadges medication={selectedMedication} />
              <div className="flex flex-wrap items-center gap-2">
                <FormularyStatusBadge
                  formulary={formulary}
                  isLoading={isFormularyFetching && !formularyQuery.data}
                  hasResponse={formularyQuery.data !== undefined}
                />
                {isFormularyFetching && formularyQuery.data ? (
                  <span
                    className="flex items-center gap-1 text-[11px] text-muted-foreground"
                    aria-live="polite"
                  >
                    <Loader2 className="size-3 animate-spin" aria-hidden />
                    Refreshing…
                  </span>
                ) : null}
                {onFormularyEligibilityOverrideChange ? (
                  <FormularyEligibilityPicker
                    eligibilities={eligibilitiesQuery.data?.items ?? []}
                    isLoading={eligibilitiesQuery.isLoading}
                    value={formularyEligibilityOverride}
                    onChange={onFormularyEligibilityOverrideChange}
                    resolvedEligibilityId={formularyQuery.data?.patientEligibilityId ?? null}
                  />
                ) : null}
                {formularyQuery.data ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    radius="pill"
                    onClick={() => setDetailOpen(true)}
                  >
                    <Eye className="size-3.5" aria-hidden />
                    <span className="text-[11px]">Full details</span>
                  </Button>
                ) : null}
                {showAlternativesButton ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    radius="pill"
                    onClick={() => setAlternativesOpen(true)}
                    disabled={isSwitching}
                    className="ml-auto"
                  >
                    <ArrowLeftRight className="size-3.5" aria-hidden />
                    <span className="text-[11px]">View alternatives ({alternativeCount})</span>
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          <AlternativesDialog
            open={alternativesOpen}
            onOpenChange={setAlternativesOpen}
            formulary={formulary}
            payerInformation={formularyQuery.data?.payerInformation}
            medication={selectedMedication}
            currentMedicationName={currentMedicationName}
            isLoading={isFormularyFetching && !formularyQuery.data}
            onSelectAlternative={selectAlternative}
            isSelectingAlternative={isSwitching}
          />
          <FormularyDetailDialog
            open={detailOpen}
            onOpenChange={setDetailOpen}
            restrictions={formulary?.Restrictions}
            copays={formulary?.Copays}
            payerInformation={formularyQuery.data?.payerInformation}
          />
        </div>
      ) : (
        <>
          <FormField
            form={form}
            type="textarea"
            name="compoundDescription"
            label="Compound"
            placeholder="Describe the compound formulation"
            textAreaProps={{ className: 'min-h-[100px]' }}
          />
          <IngredientsEditor form={form} />
          {dispenseUnitField}
        </>
      )}

      <FormField
        form={form}
        type="textarea"
        name="directions"
        label="Directions"
        placeholder="e.g. Take 1 tablet by mouth daily"
        textAreaProps={{ className: 'min-h-[96px]' }}
      />

      <div className="grid grid-cols-1 gap-3 @md:grid-cols-3">
        <FormField form={form} type="text" name="quantity" label="Qty" placeholder="e.g. 30" />
        <FormField form={form} type="text" name="refills" label="Refills" placeholder="e.g. 0" />
        <FormField
          form={form}
          type="text"
          name="daysSupply"
          label="Days supply"
          placeholder="e.g. 30"
        />
      </div>

      <PharmacySelector form={form} userId={userId} />

      <FormField
        form={form}
        type="textarea"
        name="pharmacyNotes"
        label="Notes to pharmacy"
        placeholder="Optional notes"
        textAreaProps={{ className: 'min-h-[80px]' }}
      />

      <DiagnosisSelector form={form} />

      <div className="grid grid-cols-1 gap-3 @md:grid-cols-2">
        <FormField
          form={form}
          type="text"
          name="effectiveDate"
          label="Effective date"
          placeholder="YYYY-MM-DD"
        />

        <FormField
          form={form}
          type="select"
          name="status"
          label="Status"
          options={statusOptions}
          placeholder="Select status"
        />
      </div>

      <div className="grid grid-cols-1 gap-2 @md:grid-cols-2">
        <FormField form={form} type="checkbox" name="isUrgent" label="Urgent" />
        <FormField form={form} type="checkbox" name="noSubstitutions" label="No substitutions" />
      </div>
    </div>
  );
}

interface FormularyEligibilityPickerProps {
  eligibilities: Array<{
    PatientEligibilityId?: number | null;
    PayerName?: string | null;
    PlanName?: string | null;
  }>;
  isLoading: boolean;
  value: number | null;
  onChange: (value: number | null) => void;
  /**
   * The eligibility id the server actually used on the most recent
   * formulary response. Surfaced as a hint when the picker is on
   * "Auto" so the clinician can see which coverage was selected.
   */
  resolvedEligibilityId: number | null;
}

/**
 * Optional override for the formulary lookup's ``patientEligibilityId``.
 *
 * Hidden until at least one coverage with a non-null eligibility id is
 * known — there's nothing to choose between otherwise. The default
 * "Auto" option preserves the original behaviour (server picks the
 * first active coverage that covers the channel).
 */
function FormularyEligibilityPicker({
  eligibilities,
  isLoading,
  value,
  onChange,
  resolvedEligibilityId,
}: FormularyEligibilityPickerProps) {
  const options = eligibilities.filter(
    (
      entry
    ): entry is {
      PatientEligibilityId: number;
      PayerName?: string | null;
      PlanName?: string | null;
    } => typeof entry.PatientEligibilityId === 'number'
  );

  if (isLoading || options.length === 0) {
    return null;
  }

  const selectValue =
    value !== null && options.some((entry) => entry.PatientEligibilityId === value)
      ? String(value)
      : FORMULARY_ELIGIBILITY_AUTO_VALUE;

  const handleChange = (next: string) => {
    if (next === FORMULARY_ELIGIBILITY_AUTO_VALUE) {
      onChange(null);
      return;
    }
    const parsed = Number(next);
    onChange(Number.isFinite(parsed) ? parsed : null);
  };

  const autoLabel = resolvedEligibilityId !== null ? `Auto (#${resolvedEligibilityId})` : 'Auto';

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-muted-foreground">Eligibility</span>
      <Select value={selectValue} onValueChange={handleChange}>
        <SelectTrigger size="sm" className="h-7 px-2 text-[11px]">
          <SelectValue placeholder="Auto" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={FORMULARY_ELIGIBILITY_AUTO_VALUE}>{autoLabel}</SelectItem>
          {options.map((entry) => {
            const id = entry.PatientEligibilityId;
            const label = entry.PayerName ?? entry.PlanName ?? `Coverage #${id}`;
            const detail = entry.PlanName && entry.PayerName ? entry.PlanName : null;
            return (
              <SelectItem key={id} value={String(id)}>
                <span className="flex items-baseline gap-2">
                  <span>{label}</span>
                  {detail ? (
                    <span className="text-[10px] text-muted-foreground">{detail}</span>
                  ) : null}
                  <span className="font-mono text-[10px] text-muted-foreground">#{id}</span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
